/**
 * TrackingNotInstalledBanner — soft, self-clearing "your script isn't live" state.
 *
 * A site is created before the tracking script is ever verified, so an account
 * can sit in a state where every surface reads zero and nothing says why. This
 * is the one place that says it out loud.
 *
 * Deliberately NOT dismissible: unlike GenerationBanner (informational, snoozes
 * fine) this describes a broken install. It clears on its own the moment the
 * first pageview lands, so there is nothing for the user to remember.
 *
 * Shares GenerationBanner's markup contract so the two stack as one system.
 */
import { useNavigate } from "react-router-dom"
import { HugeiconsIcon } from "@hugeicons/react"
import { AlertCircleIcon } from "@hugeicons/core-free-icons"

import { Button } from "@/components/ui/button"
import { track } from "@/lib/utils/track"
import { useAuth } from "@/lib/contexts/AuthContext"

export interface UninstalledSite {
  propertyId: string
  domain: string
}

interface TrackingNotInstalledBannerProps {
  sites: UninstalledSite[]
  /** Scroll target for the multi-site case, where the per-row pill owns the action. */
  onReviewSites?: () => void
}

export default function TrackingNotInstalledBanner({
  sites,
  onReviewSites,
}: TrackingNotInstalledBannerProps) {
  const navigate = useNavigate()
  const { token } = useAuth()

  if (sites.length === 0) return null

  const single = sites.length === 1 ? sites[0] : null

  const handleClick = () => {
    track("tracking_not_installed_cta_clicked", token, {
      site_count: sites.length,
      property_id: single?.propertyId,
    })
    if (single) {
      navigate(
        `/onboarding/install-script?propertyId=${encodeURIComponent(single.propertyId)}&domain=${encodeURIComponent(single.domain)}`
      )
      return
    }
    onReviewSites?.()
  }

  return (
    <div className="flex items-center gap-3 rounded-xl bg-warning-tint px-4 py-3 shadow-[0_0_0_1px_color-mix(in_oklab,var(--warning)_18%,transparent)]">
      <div className="shrink-0 self-start pt-px">
        <HugeiconsIcon icon={AlertCircleIcon} size={16} className="text-warning" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium leading-4 text-foreground">
          {single
            ? <>No data received from <span className="tabular-nums">{single.domain}</span> yet</>
            : `No data received from ${sites.length} sites yet`}
        </p>
        <p className="mt-1 text-xs leading-4 text-muted-foreground">
          Analytics, heatmaps, and A/B tests stay empty until the Humblytics script
          is live on the page. This notice clears automatically once we see a pageview.
        </p>
      </div>

      {/* Ink, not orange: Add site keeps the view's one orange CTA. */}
      <Button
        size="sm"
        onClick={handleClick}
        className="h-8 shrink-0 rounded-lg bg-foreground px-3 text-[13px] font-medium text-background hover:bg-foreground/90"
      >
        {single ? "Install tracker" : "Review sites"}
      </Button>
    </div>
  )
}
